import { Button, Checkbox as MuiCheckbox, FormControlLabel, MenuItem } from '@mui/material';
import { useField, Field } from 'formik';
import { FC } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { fontSizeValueSchema, hexColorValidationSchema, useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, Checkbox } from '../../types/form-fields';
import {
  CheckboxField as CheckboxFormField,
  ColorPickerField,
  FormRow,
  InputField as FormikInputField,
  InputField,
} from '../fields';
import { FormBlock } from './common';

export const isCheckboxField = (field: FormField): field is Checkbox => {
  return field.type === FieldType.Checkbox;
};

export const defaultCheckboxFieldProps: Checkbox = {
  type: FieldType.Checkbox,
  label: 'I agree to my personal data being stored and used to receive the newsletter',
  defaultValue: true,
  required: true,
  fontWeight: 400,
  color: '#ececec',
  fontSize: 14,
  labelAsHTML: false,
};

type CheckboxFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

const textValueSchema = Yup.string().optional().max(255, 'Should be < 255 characters');

const CheckboxFieldSettings: FC<CheckboxFieldSettingsProps> = ({ name, remove }) => {
  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  const [field] = useField<Checkbox>(name);

  const { t } = useTranslation();
  const labelAsHTML = field.value?.labelAsHTML;

  return (
    <CheckboxFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.label`}
          component={FormikInputField}
          label={labelAsHTML ? t('pages.campaign.checkboxField.textHtml') : t('pages.campaign.checkboxField.text')}
          multiline
          rows={3}
          maxRows={5}
          validate={(value: string) => validate(value, textValueSchema)}
        />

        <Field
          name={`${name}.labelAsHTML`}
          component={CheckboxFormField}
          label={t('pages.campaign.checkboxField.useAsHtml')}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.color`}
          component={ColorPickerField}
          label={t('pages.campaign.checkboxField.color')}
          validate={(value: string) => validate(value, hexColorValidationSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.fontWeight`}
          component={InputField}
          label={t('pages.campaign.checkboxField.fontWeight')}
          select
          defaultValue="400"
        >
          {[300, 400, 500, 700].map((fontWeight) => (
            <MenuItem key={fontWeight} value={fontWeight}>
              {fontWeight}
            </MenuItem>
          ))}
        </Field>
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.fontSize`}
          component={InputField}
          label={t('pages.campaign.checkboxField.fontSize')}
          type="number"
          validate={(value: string) => validate(value, fontSizeValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.defaultValue`}
          component={CheckboxFormField}
          label={t('pages.campaign.checkboxField.defaultChecked')}
        />
      </FormRow>
      <FormRow>
        <Field
          name={`${name}.required`}
          component={CheckboxFormField}
          label={t('pages.campaign.checkboxField.required')}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.name`}
          component={FormikInputField}
          label={t('pages.campaign.checkboxField.name')}
          validate={(value: string) => validate(value, textValueSchema)}
          helperText={t('pages.campaign.checkboxField.nameInfo')}
        />
      </FormRow>

      {remove && (
        <Button color="error" onClick={remove}>
          {t('pages.campaign.checkboxField.buttonRemove')}
        </Button>
      )}
    </CheckboxFieldSettingsWrapper>
  );
};

type CheckboxFieldProps = {
  name: string;
  remove?: () => void;
};

export const CheckboxField: FC<CheckboxFieldProps> = ({ name, remove }) => {
  const [field] = useField<Checkbox>(name);
  const dispatch = useAppDispatch();

  const {
    value: { defaultValue, label, fontWeight, color, fontSize, labelAsHTML },
  } = field;

  const handleClick = () => {
    dispatch(
      openSidebar({
        content: <CheckboxFieldSettings name={name} remove={remove} />,
      })
    );
  };

  return (
    <FormBlock onClick={handleClick} style={{ margin: '-6px 0' }}>
      <Cover />

      <CheckboxLabel
        disableTypography
        control={<MuiCheckbox checked={defaultValue} style={{ color }} size="small" />}
        label={
          <span
            style={{ fontWeight, whiteSpace: 'pre-line', fontSize: `${fontSize}px`, marginTop: '8px' }}
            dangerouslySetInnerHTML={labelAsHTML ? { __html: label } : undefined}
          >
            {labelAsHTML ? null : label}
          </span>
        }
        style={{ color, pointerEvents: 'none' }}
      />
    </FormBlock>
  );
};

const CheckboxFieldSettingsWrapper = styled.div``;

const CheckboxLabel = styled(FormControlLabel)`
  align-items: flex-start;

  a,
  a:visited {
    color: inherit;
  }
`;

const Cover = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0 0 0 0;
  cursor: pointer;
  pointer-events: none;
`;
